import type { RequestOptions } from '../client.js';
import {
  type BalanceData,
  type TokenMetadataData,
  type TokenAllowanceData,
  getBalanceParamsSchema,
  getTokenBalanceParamsSchema,
  getTokenMetadataParamsSchema,
  getTokenAllowanceParamsSchema,
} from '@spectrum-nodes/api-spec/models';
import type { ChainSlug, EvmChainSlug, CosmosChainSlug, SolanaChainSlug } from '../types/chains.js';
import type { HistoricalQueryOptions } from '../types/client.js';
import { parseParams } from '../internals/validation.js';
import { splitHistoricalArgs } from '../internals/utils.js';
import { ChainNamespace } from './base.js';

/** Token balances and metadata */
export class TokensNamespace extends ChainNamespace {
  /**
   * Get the native token balance for an address.
   */
  async getBalance(
    chain: ChainSlug | undefined,
    address: string,
    paramsOrOptions?: HistoricalQueryOptions | RequestOptions,
    options?: RequestOptions,
  ): Promise<BalanceData> {
    const { params, options: requestOptions } = splitHistoricalArgs(paramsOrOptions, options);
    const validated = parseParams(getBalanceParamsSchema, {
      chain: this.resolve(chain),
      address,
      blockHeight: params?.blockHeight,
    });
    return this.client.jsonRpc<BalanceData>('getBalance', validated, requestOptions);
  }

  /**
   * Get a specific ERC-20/SPL token balance for an address.
   */
  async getTokenBalance(
    chain: ChainSlug | undefined,
    address: string,
    token: string,
    paramsOrOptions?: HistoricalQueryOptions | RequestOptions,
    options?: RequestOptions,
  ): Promise<BalanceData> {
    const { params, options: requestOptions } = splitHistoricalArgs(paramsOrOptions, options);
    const validated = parseParams(getTokenBalanceParamsSchema, {
      chain: this.resolve(chain),
      address,
      token,
      blockHeight: params?.blockHeight,
    });
    return this.client.jsonRpc<BalanceData>('getTokenBalance', validated, requestOptions);
  }

  /**
   * Get token metadata. EVM chains return name/symbol/decimals/total supply; Cosmos
   * chains return denom metadata (pass the denom as `tokenAddress`); Solana returns
   * name/symbol/decimals/supply/uri from Metaplex or the Token-2022 extension.
   */
  async getMetadata(
    chain: EvmChainSlug | CosmosChainSlug | SolanaChainSlug | undefined,
    tokenAddress: string,
    paramsOrOptions?: HistoricalQueryOptions | RequestOptions,
    options?: RequestOptions,
  ): Promise<TokenMetadataData> {
    const { params, options: requestOptions } = splitHistoricalArgs(paramsOrOptions, options);
    const validated = parseParams(getTokenMetadataParamsSchema, {
      chain: this.resolve(chain),
      address: tokenAddress,
      blockHeight: params?.blockHeight,
    });
    return this.client.jsonRpc<TokenMetadataData>('getTokenMetadata', validated, {
      ...requestOptions,
      cacheTtl: requestOptions?.cacheTtl ?? 3600_000,
    });
  }


  /** Get ERC-20 token allowance. EVM only. */
  async getAllowance(
    chain: EvmChainSlug | undefined,
    tokenAddress: string,
    params: { owner: string; spender: string; blockHeight?: number },
    options?: RequestOptions,
  ): Promise<TokenAllowanceData> {
    const validated = parseParams(getTokenAllowanceParamsSchema, {
      chain: this.resolve(chain),
      address: tokenAddress,
      owner: params.owner,
      spender: params.spender,
      blockHeight: params.blockHeight,
    });
    return this.client.jsonRpc<TokenAllowanceData>('getTokenAllowance', validated, options);
  }
}
